import { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { AuthProvider as AuthProviderType, AuthResult } from '../services/authService';

// Define user roles
export type UserRole = 'user' | 'superuser';

export interface User {
  id: string;
  username: string;
  name: string;
  role: UserRole;
  email: string;
  permissions: Permission[];
  provider?: AuthProviderType; // OAuth provider used for authentication
}

export type Permission = 
  | 'view_documents'
  | 'download_documents'
  | 'send_reminders'
  | 'manage_templates'
  | 'new_request'
  | 'new_request_templates'
  | 'view_request_templates'
  | 'assign_templates'
  | 'manage_users';

// Define what permissions each role has
const rolePermissions: Record<UserRole, Permission[]> = {
  user: ['view_documents', 'new_request', 'send_reminders'],
  superuser: [
    'view_documents', 
    'download_documents', 
    'send_reminders',
    'manage_templates',
    'new_request',
    'new_request_templates',
    'assign_templates',
    'manage_users'
  ]
};

// Mock users for demo purposes
const mockUsers: User[] = [
  {
    id: '1',
    username: 'regular_user',
    name: 'Regular User',
    role: 'user',
    email: 'user@yourCompany.com',
    permissions: rolePermissions.user
  },
  {
    id: '2',
    username: 'super_user',
    name: 'Super User',
    role: 'superuser',
    email: 'superUser@yourCompany.com',
    permissions: rolePermissions.superuser
  },
  {
    id: '3',
    username: 'msanders',
    name: 'Michelle Sanders',
    role: 'superuser',
    email: 'msanders@yourCompany.com',
    permissions: rolePermissions.superuser
  }
];

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasPermission: (permission: Permission) => boolean;
  login: (username: string, password: string) => Promise<boolean>;
  loginWithOAuth: (authResult: AuthResult) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  // Save user to localStorage when it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('currentUser');
    }
  }, [currentUser]);

  // Check if user has a specific permission
  const hasPermission = (permission: Permission): boolean => {
    if (!currentUser) return false;
    return currentUser.permissions.includes(permission);
  };

  // Mock login function
  const login = async (username: string, _password: string): Promise<boolean> => {
    setIsLoading(true);
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 800));

    // Find user (in a real app, this would verify password with backend)
    const user = mockUsers.find(u => u.username === username);
    
    if (user) {
      setCurrentUser(user);
      setIsLoading(false);
      return true;
    }
    
    setIsLoading(false);
    return false;
  };

  // OAuth login function
  const loginWithOAuth = async (authResult: AuthResult): Promise<boolean> => {
    setIsLoading(true);
    
    if (!authResult.success) {
      setIsLoading(false);
      return false;
    }

    // In a real app, you would validate the OAuth token with your backend
    // and create or retrieve the user account
    // For demo purposes, we'll find or create a user based on email
    let user = mockUsers.find(u => u.email === authResult.email);
    
    if (!user && authResult.email && authResult.name) {
      // Create a new user for OAuth authentication
      user = {
        id: `oauth_${Date.now()}`,
        username: authResult.email.split('@')[0],
        name: authResult.name,
        role: 'user',
        email: authResult.email,
        permissions: rolePermissions.user,
        provider: authResult.provider
      };
      mockUsers.push(user);
    }
    
    if (user) {
      // Update provider info
      user.provider = authResult.provider;
      setCurrentUser(user);
      setIsLoading(false);
      return true;
    }
    
    setIsLoading(false);
    return false;
  };

  // Logout function
  const logout = () => {
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated: !!currentUser,
        isLoading,
        hasPermission,
        login,
        loginWithOAuth,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
